import { useMemo } from 'react';
import { useGet } from '@/query/fetchHooks';
import {
  CloseCircleFilled,
  TaskCheckmarkFilled,
  Time,
} from '@teo/components/icons';
import { PersonAvatar } from '@teo/components';
import {
  SparklesIcon,
  ExpiredIcon,
} from '@/components/Competences/SparklesIcon';
import { useTranslation } from 'react-i18next';

const FIRST_COLUMN_MIN_WIDTH = 320;
const USER_COLUMN_MIN_WIDTH = 110;

const CompetenceMatrixCell = ({ item }) => {
  const isCompleted = Boolean(item.isCompleted);
  const isExempted = item.assignmentMethod === 'exemption';
  const isDenied = item.assignmentMethod === 'denied';
  const hasExpired = item.hasExpired;

  return (
    <>
      {hasExpired ? (
        <span className="flex h-[26px] w-[26px] items-center justify-center rounded-full bg-[#FB8C00] text-white">
          <ExpiredIcon className="h-4 w-4" />
        </span>
      ) : isDenied ? (
        <CloseCircleFilled className="h-8 w-8 text-error-05" />
      ) : isExempted ? (
        <span className="flex h-[26px] w-[26px] items-center justify-center rounded-full bg-primary-05 text-white">
          <SparklesIcon className="h-4 w-4" />
        </span>
      ) : isCompleted ? (
        <TaskCheckmarkFilled className="h-8 w-8 text-success-05" />
      ) : (
        <span className="flex h-[26px] w-[26px] items-center justify-center rounded-full bg-grey-03 text-white">
          <Time className="h-4 w-4 text-grey-06" />
        </span>
      )}
    </>
  );
};

const MatrixRow = ({ competence, users, matrix, isLast }) => {
  return (
    <>
      <div
        className={`
          sticky left-0 z-20
          flex h-[58px] min-w-0 items-center gap-3
          border-r 
          bg-white px-4
          ${isLast ? '' : 'border-b'}
        `}
      >
        <span
          className="h-1.5 w-1.5 shrink-0 rounded-full"
          style={{
            backgroundColor: competence.colorCode || '#64748B',
          }}
        />
        <span className="shrink-0 whitespace-nowrap text-xs text-grey-06">
          {competence.code}
        </span>

        <span
          className="min-w-0 flex-1 truncate text-sm font-medium text-[#48484D]"
          title={competence.name}
        >
          {competence.name}
        </span>
      </div>

      {users.map((user) => {
        const item = matrix.get(
          `${competence.professionCompetenceId}:${user.userId}`
        );

        return (
          <div
            key={`${competence.professionCompetenceId}-${user.userId}`}
            className={`
              flex h-[58px] min-w-0
              items-center justify-center
              bg-white
              ${isLast ? '' : 'border-b'}
            `}
          >
            <CompetenceMatrixCell item={item} />
          </div>
        );
      })}
    </>
  );
};

export const TeamProfessionCompetenceMatrix = ({ teamId, professionId }) => {
  const result = useGet('/teamprofessioncompetencematrixdetails', {
    teamId,
    professionId,
  });

  const { t } = useTranslation();

  const data = useMemo(() => {
    if (Array.isArray(result)) {
      return result;
    }

    if (Array.isArray(result?.data?.data)) {
      return result.data.data;
    }

    return [];
  }, [result]);

  const users = useMemo(() => {
    const usersMap = new Map();

    data.forEach((item) => {
      if (!usersMap.has(item.userId)) {
        usersMap.set(item.userId, {
          userId: item.userId,
          username: item.username,
          studentProfessionId: item.studentProfessionId,
        });
      }
    });

    return Array.from(usersMap.values());
  }, [data]);

  const competencies = useMemo(() => {
    const competenciesMap = new Map();

    data.forEach((item) => {
      if (!competenciesMap.has(item.professionCompetenceId)) {
        competenciesMap.set(item.professionCompetenceId, {
          professionCompetenceId: item.professionCompetenceId,
          competenceId: item.competenceId,
          code: item.code,
          path: item.path,
          description: item.description,
          colorCode: item.colorCode,
          name: item.name,
        });
      }
    });

    return Array.from(competenciesMap.values());
  }, [data]);

  const matrix = useMemo(() => {
    const matrixMap = new Map();

    data.forEach((item) => {
      matrixMap.set(`${item.professionCompetenceId}:${item.userId}`, item);
    });

    return matrixMap;
  }, [data]);

  const userProgress = useMemo(() => {
    const progressMap = new Map();

    users.forEach((user) => {
      const userItems = data.filter((item) => item.userId === user.userId);

      if (!userItems.length) {
        progressMap.set(user.userId, 0);
        return;
      }

      const completedItems = userItems.filter(
        (item) =>
          item.isCompleted ||
          (item.decisionApplied && item.decisionOutcome === 'granted')
      );

      const percentage = Math.round(
        (completedItems.length / userItems.length) * 100
      );

      progressMap.set(user.userId, percentage);
    });

    return progressMap;
  }, [data, users]);

  if (!data.length) {
    return (
      <div className="flex min-h-[200px] w-full items-center justify-center rounded-lg border border-[#E6E6E9] bg-white text-sm text-[#7D7D81]">
        {t('pages.follow_up_users.no_competencies')}
      </div>
    );
  }

  const tableMinWidth =
    FIRST_COLUMN_MIN_WIDTH + users.length * USER_COLUMN_MIN_WIDTH;

  return (
    <div className="w-full min-w-0 overflow-hidden border-t bg-white">
      <div className="relative w-full min-w-0 overflow-hidden">
        <div className="w-full min-w-0 overflow-x-auto overflow-y-hidden">
          <div
            className="grid"
            style={{
              width: `max(100%, ${tableMinWidth}px)`,
              gridTemplateColumns: `
              ${FIRST_COLUMN_MIN_WIDTH}px
              repeat(
                ${users.length},
                minmax(${USER_COLUMN_MIN_WIDTH}px, 1fr)
              )
            `,
            }}
          >
            <div
              className="
              sticky left-0 top-0 z-30
              flex h-[120px] items-end
              border-b border-r border-[#E6E6E9]
              bg-white px-4 pb-4
            "
            >
              <span className="text-xs font-semibold uppercase tracking-[0.08em] text-[#7D7D81]">
                {t('columns.competence')}
              </span>
            </div>

            {users.map((user) => {
              const percentage = userProgress.get(user.userId) || 0;

              return (
                <div
                  key={user.userId}
                  className="
                  flex h-[120px] min-w-0
                  flex-col items-center justify-end
                  gap-2 border-b border-[#E6E6E9]
                  bg-white px-2 pb-3
                "
                >
                  <PersonAvatar
                    inline
                    id={user.userId}
                    name={user.username}
                    imageSrc={user.picture}
                  />

                  <div
                    className="
                    w-full min-w-0 truncate
                    text-center text-xs
                    font-medium text-[#56565B]
                  "
                    title={user.username}
                  >
                    {user.username}
                  </div>

                  <div
                    className={`text-xs font-semibold ${
                      percentage === 100
                        ? 'text-[#46B96C]'
                        : percentage >= 70
                        ? 'text-[#64B979]'
                        : percentage >= 50
                        ? 'text-[#B39836]'
                        : 'text-[#D75B50]'
                    }`}
                  >
                    {percentage}%
                  </div>
                </div>
              );
            })}

            {competencies.map((competence, index) => (
              <MatrixRow
                key={competence.professionCompetenceId}
                competence={competence}
                users={users}
                matrix={matrix}
                isLast={index === competencies.length - 1}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
